import type { CarrierAddressNeedsManualDetails } from '@/lib/api/carrier-shipment-api'

import { CARRIER_ADDRESS_NEEDS_MANUAL } from '@/lib/api/carrier-shipment-api'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'

/** What the form should do with a failed quote or booking call. */
export type CarrierFailureOutcome =
    /** The address has no carrier mapping: open the manual unit picker. */
    | { kind: 'manual'; provinceId: string }
    /** Tell the operator; `inline` keeps the message next to the quote button. */
    | { kind: 'error'; message: string; inline: boolean }

export function describeCarrierFailure(
    err: unknown,
    isQuote: boolean,
): CarrierFailureOutcome {
    const fallback = isQuote
        ? m.carrier_form_quote_failed()
        : m.carrier_form_submit_failed()
    if (!(err instanceof ApiError)) {
        return { kind: 'error', message: fallback, inline: false }
    }
    if (err.code === CARRIER_ADDRESS_NEEDS_MANUAL) {
        const details = (err.details ?? {}) as CarrierAddressNeedsManualDetails
        return {
            kind: 'manual',
            provinceId: details.vtp_province_id
                ? String(details.vtp_province_id)
                : '',
        }
    }
    const carrierMessage = (
        err.details as { carrier_message?: unknown } | undefined
    )?.carrier_message
    // Prefer the new envelope; keep legacy details during the BE transition.
    const rejectedMessage =
        resolveApiErrorMessage(err.code, err.params) ??
        (typeof carrierMessage === 'string' && carrierMessage
            ? resolveApiErrorMessage('CARRIER_REJECTED', { carrierMessage })
            : null)
    const message =
        err.code === 'CARRIER_REJECTED'
            ? (rejectedMessage ?? m.carrier_form_rejected_without_details())
            : isQuote && err.status === 400
              ? m.carrier_form_weight_invalid()
              : (resolveApiErrorMessage(err.code, err.params) ??
                (isQuote ? fallback : err.message || fallback))
    const inline =
        err.code === 'CARRIER_CONFIG_ERROR' ||
        err.code === 'CARRIER_UNAVAILABLE'
    return { kind: 'error', message, inline }
}
